import { queryOptions, UseQueryOptions } from "@tanstack/react-query";
import { api, mBaseURL } from "@/config/api";
import { APIResponseSuccessSchema } from "@/schema/api";
import { Product, ProductSchema } from "@/schema/knowledge/products";
import { CompanyProfileSchema } from "@/schema/profile";
import { unwrapApiError } from "@/lib/error";
import { createFormData, createQueryString, createURLPath, sanitizeFileName } from "@/lib/utils";
import { z } from "zod";

type QueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export interface CreateResearchBrandGuidelinesParams {
  brand_name: string;
  brand_url: string;
}

export interface UpdateBrandGuidelineNodeParams {
  node: string;
  value: string;
}

export interface FetchBrandProductsParams extends Record<string, string | number | boolean | null | undefined> {
  page: number;
  size: number;
}

export interface FetchBrandProductsResponse {
  items: Product[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export const KnowledgeApiFactory = {
  Endpoint: mBaseURL + "/api/knowledge",
  Keys: {
    FetchBrandProducts: (params: FetchBrandProductsParams) => ["knowledge", "products", createQueryString(params)],
    FetchInfiniteBrandProducts: (params: Omit<FetchBrandProductsParams, "page" | "size">) => ["knowledge", "products", "infinite", createQueryString(params)],
    CreateBrandGuidelinesResearch: () => ["knowledge", "brand", "guidelines", "research"],
    UploadBrandGuidelines: () => ["knowledge", "brand", "guidelines", "upload"],
  },
  Schemas: {
    Response: {
      UpdateBrandGuidelineKnowledge: APIResponseSuccessSchema.extend({
        data: CompanyProfileSchema,
      }),
      UploadBrandGuidelines: APIResponseSuccessSchema.extend({
        data: CompanyProfileSchema,
      }),
      FetchBrandProducts: APIResponseSuccessSchema.extend({
        data: z.object({
          items: ProductSchema.array(),
          total: z.number(),
          page: z.number(),
          size: z.number(),
          pages: z.number(),
        }),
      }),
    },
  },
  Apis: {
    FetchBrandProducts: async (params: FetchBrandProductsParams) => {
      const response = await api(KnowledgeApiFactory.Endpoint + "/brand/products?" + createQueryString(params));

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response
        .json()
        .then(KnowledgeApiFactory.Schemas.Response.FetchBrandProducts.parse)
        .then((data) => data.data);
    },
    CreateBrandGuidelinesResearch: async (body: CreateResearchBrandGuidelinesParams) => {
      const response = await api(createURLPath(KnowledgeApiFactory.Endpoint, "brand", "guidelines", "research"), {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw error;
      }

      return response;
    },
    UploadBrandGuidelines: async (file: File) => {
      const form = createFormData({ file: [file, sanitizeFileName(file.name)] });

      const response = await api(createURLPath(KnowledgeApiFactory.Endpoint, "brand", "guidelines", "upload"), {
        method: "POST",
        multipart: true,
        body: form,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(KnowledgeApiFactory.Schemas.Response.UploadBrandGuidelines.parse);
    },
  },
  Queries: {
    FetchBrandProducts: (params: FetchBrandProductsParams, options?: QueryOptions<FetchBrandProductsResponse>) => {
      return queryOptions({
        queryKey: KnowledgeApiFactory.Keys.FetchBrandProducts(params),
        queryFn: () => KnowledgeApiFactory.Apis.FetchBrandProducts(params),
        ...options,
      });
    },
  },
};
